import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { useOrg } from '../OrgProvider';
import { fetchCiConfig, fetchCiModels, updateCiConfig } from '../api';
import type { OrgCiConfig } from '../types';
import type { IndexModelOption } from '@/lib/openrouter-models';
import { ModelPicker } from './ModelPicker';
import { canManageOrg } from '../org-roles';

/**
 * Settings a CI runner picks up at start-up.
 *
 * The point of storing these server-side is that a CI job then holds exactly one
 * secret — its org API key — and never needs a database URL or storage
 * credentials. This panel is where that config is set.
 *
 * The LLM key is write-only: the API returns whether one exists and its last
 * four characters, never the value, so it cannot be read back out through the
 * browser once saved.
 */
export function CiConfigPanel() {
  const { getToken } = useAuth();
  const { activeOrg } = useOrg();
  // Members see the settings their runners get; owners and admins change them.
  const canManage = canManageOrg(activeOrg);

  const [config, setConfig] = useState<OrgCiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Draft state. `llmKey` empty means "leave the stored key alone" — it is only
  // sent when the operator actually types a new one.
  const [llmKey, setLlmKey] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [retainVersions, setRetainVersions] = useState('');
  const [models, setModels] = useState<IndexModelOption[] | null>(null);
  const [recommendedModel, setRecommendedModel] = useState<string | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      // The model list is a convenience: its failure falls back to a text
      // field instead of failing the panel.
      void fetchCiModels(activeOrg.id, token).then(
        (r) => {
          setModels(r.models);
          setRecommendedModel(r.recommended);
          setModelsError(null);
        },
        (err: unknown) => setModelsError(err instanceof Error ? err.message : String(err))
      );
      const next = await fetchCiConfig(activeOrg.id, token);
      setConfig(next);
      setLlmModel(next.llmIndexModel ?? '');
      setNamePrefix(next.ciNamePrefix ?? '');
      setRetainVersions(next.ciRetainVersions == null ? '' : String(next.ciRetainVersions));
      setLlmKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load CI config.');
    } finally {
      setLoading(false);
    }
  }, [activeOrg, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(patch: {
    llmApiKey?: string | null;
    llmIndexModel?: string | null;
    ciEnabled?: boolean;
    ciNamePrefix?: string | null;
    ciRetainVersions?: number | null;
  }) {
    if (!activeOrg) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const next = await updateCiConfig(activeOrg.id, patch, token);
      setConfig(next);
      setLlmModel(next.llmIndexModel ?? '');
      setNamePrefix(next.ciNamePrefix ?? '');
      setRetainVersions(next.ciRetainVersions == null ? '' : String(next.ciRetainVersions));
      setLlmKey('');
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save CI config.');
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const retain = retainVersions.trim();
    void save({
      // Omitted entirely when blank, so saving the model doesn't wipe the key.
      ...(llmKey.trim() ? { llmApiKey: llmKey.trim() } : {}),
      llmIndexModel: llmModel.trim() || null,
      ciNamePrefix: namePrefix.trim() || null,
      // Blank means "keep everything" — an explicit choice, distinct from unset.
      ciRetainVersions: retain === '' ? null : Number(retain),
    });
  }

  if (!activeOrg) return null;

  return (
    <div className="space-y-4 border border-rule-soft bg-panel p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-ink">CI indexing</h3>
          <p className="mt-1 text-xs text-dim">
            Settings a CI job picks up from its API key. With these set, a runner needs only{' '}
            <code className="font-mono text-2xs">LLMCONTEXT_URL</code> and{' '}
            <code className="font-mono text-2xs">LLMCONTEXT_API_KEY</code> — never a database URL.
          </p>
        </div>
        {config ? (
          <label className="flex shrink-0 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={config.ciEnabled}
              disabled={saving || !canManage}
              onChange={(e) => void save({ ciEnabled: e.target.checked })}
              className="h-4 w-4 rounded border-rule text-accent focus:ring-accent"
            />
            <span className="text-xs font-medium text-link">Enabled</span>
          </label>
        ) : null}
      </div>

      {loading ? (
        <p className="text-xs text-dim">Loading…</p>
      ) : (
        <>
          {config && !config.ciEnabled ? (
            <p className="border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
              CI indexing is turned off for this organization. Runs are rejected, but the API key
              still works for queries.
            </p>
          ) : null}

          {!canManage && (
            <p className="text-xs text-dim">
              Only organization owners and admins can change these settings.
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <fieldset disabled={!canManage} className="min-w-0 space-y-3">
              <label className="flex flex-col gap-1">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  LLM provider key
                </span>
                <input
                  type="password"
                  value={llmKey}
                  onChange={(e) => setLlmKey(e.target.value)}
                  placeholder={
                    config?.hasLlmKey
                      ? `Stored — ends ${config.llmKeyHint ?? '••••'}. Type to replace.`
                      : 'sk-or-…'
                  }
                  className="lc-input py-1.5 font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                />
                <span className="text-2xs text-dim">
                  Encrypted before storage and never returned by a read. Use a key scoped to CI so
                  it can be revoked on its own.
                  {config?.hasLlmKey ? ' Leave blank to keep the current one.' : ''}
                </span>
              </label>

              <div className="flex flex-col gap-1">
                <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                  Index model
                </span>
                <ModelPicker
                  value={llmModel}
                  onChange={setLlmModel}
                  models={models}
                  recommended={recommendedModel}
                  loadError={modelsError}
                  disabled={saving}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                    Package name prefix <span className="normal-case">(optional)</span>
                  </span>
                  <input
                    value={namePrefix}
                    onChange={(e) => setNamePrefix(e.target.value)}
                    placeholder="e.g. acme/"
                    className="lc-input py-1.5 font-mono text-xs"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <span className="text-2xs text-dim">
                    Forces every CI-pushed name into your own namespace. Without one, pushes that
                    collide with a public package name are rejected — org packages outrank public
                    ones in retrieval, so an org-scoped <code className="font-mono">react</code>{' '}
                    would answer every react question here.
                  </span>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-3xs font-medium uppercase tracking-wide text-dim">
                    Versions kept per package
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={retainVersions}
                    onChange={(e) => setRetainVersions(e.target.value)}
                    placeholder="blank = keep every version"
                    className="lc-input py-1.5 font-mono text-xs"
                  />
                  <span className="text-2xs text-dim">
                    CI records a version per commit. Older ones are deleted along with their chunks
                    and vectors once this many newer versions exist.
                  </span>
                </label>
              </div>

              {config?.ciAllowPublicShadow ? (
                <p className="border border-warn/40 bg-warn/10 px-3 py-2 text-2xs text-warn">
                  Public-name shadowing is allowed for this organization. A CI push can take over a
                  public package name for every query made here.
                </p>
              ) : null}

              {canManage && (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-2xs text-dim">
                    {config?.hasLlmKey ? (
                      <button
                        type="button"
                        onClick={() => void save({ llmApiKey: null })}
                        disabled={saving}
                        className="text-del hover:underline disabled:opacity-50"
                      >
                        Remove stored key
                      </button>
                    ) : (
                      'No key stored — runners must supply their own.'
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {savedAt ? <span className="text-2xs text-good">Saved</span> : null}
                    <Button type="submit" size="sm" disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              )}
            </fieldset>
          </form>

          {error ? <p className="text-xs text-del">{error}</p> : null}
        </>
      )}
    </div>
  );
}
